import { AccountService, AuthService } from './core';
import { catchError, Observable, of, switchMap, tap } from 'rxjs';
import { getSubdomain } from './shared/Utils/common.utils';
import { Store } from '@ngxs/store';
import { AccountActions } from './state/account/actions';
import { SetUser, SetUsers } from './state/user/actions';
import { ObjectsManagerActions } from './modules/settings/objects-manager/state/actions';
import { AllEnterpriseModule, LicenseManager, ModuleRegistry, provideGlobalGridOptions } from 'ag-grid-enterprise';
import { themeQuartz } from 'ag-grid-community';
import { FeatureFlag } from './core/enums';
import { environment } from '@env/environment';

ModuleRegistry.registerModules([AllEnterpriseModule]);

LicenseManager.setLicenseKey(environment.agGridLicenseKey);

// Global Ag-Grid theme options.
provideGlobalGridOptions({
  theme: themeQuartz.withParams({
    accentColor: '#207CEC',
    headerBackgroundColor: '#ffffff',
    wrapperBorderRadius: 0,
  }),
});

export function appInitializer(
  authService: AuthService,
  accountService: AccountService,
  store: Store,
): () => Observable<any> {
  return () => {
    const subdomain = getSubdomain();

    if (!subdomain) {
      return authService.check().pipe(
        switchMap((isAuthenticated) => {
          if (!isAuthenticated) {
            return of(null);
          }
          return accountService.getUser().pipe(tap((user) => store.dispatch(new SetUser(user))));
        }),
        catchError(() => of(null)),
      );
    }

    // SC-29360 - Clearing the old task filters from local storage
    localStorage.removeItem('projectTasksQueryFilters');
    localStorage.removeItem('propertyTasksQueryFilters');
    localStorage.removeItem('globalTaskQueryFilters');

    return authService.check().pipe(
      switchMap((isAuthenticated) => {
        if (isAuthenticated) {
          return accountService.getAccount(subdomain).pipe(
            tap((account) => {
              accountService.setAccount(account);

              if (account.features.includes(FeatureFlag.NEW_NAVIGATION)) {
                document.documentElement.style.setProperty('--max-height-content', '100%');
              } else {
                document.documentElement.style.setProperty('--max-height-content', 'calc(100vh - 64px)');
              }

              return store.dispatch(new AccountActions.SetAccount(account));
            }),
            switchMap(() => {
              return store.dispatch(new ObjectsManagerActions.Get());
            }),
            switchMap(() => {
              return accountService.getUser();
            }),
            switchMap((user) => {
              store.dispatch(new SetUser(user));
              return accountService.getUsers();
            }),
            tap((users) => {
              store.dispatch(new SetUsers(users));
            }),
          );
        }

        return of(null);
      }),
    );
  };
}
